import { useState, useCallback } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { getAdminPermissions } from "@/shared/config/adminPermissions";
import AdminSidebar, { AdminView } from "./components/AdminSidebar";
import AdminDashboardView from "./views/AdminDashboardView";
import BillingView from "./views/BillingView";
import EntriesView from "./views/EntriesView";
import AdminCalendarView from "./views/AdminCalendarView";
import AdminSupportView from "./views/AdminSupportView";
import FAQView from "@/shared/components/FAQ";
import styles from "../attorney/Attorney.module.css"; // Shared layout styles
import adminStyles from "./Admin.module.css";

interface AdminPageProps {
  onSwitchToAttorney?: () => void;
}

/**
 * Admin Dashboard Page
 *
 * Main entry point for firm administrators.
 * Shows different views based on admin permissions.
 */
export default function AdminPage({ onSwitchToAttorney }: AdminPageProps) {
  const { user } = useAuth();
  const { darkMode } = useDarkMode();
  const { canView } = getAdminPermissions(user?.email);

  // Determine initial view based on permissions
  const getInitialView = (): AdminView => {
    if (canView.overview) return "dashboard";
    if (canView.billing) return "billing";
    if (canView.entries) return "entries";
    if (canView.calendar) return "calendar";
    if (canView.support) return "support";
    return "dashboard"; // Fallback
  };

  const [currentView, setCurrentView] = useState<AdminView>(getInitialView);
  const [sidebarOpen] = useState(true);

  /**
   * Navigate from Calendar view to Entries view with pre-populated filters.
   * Updates persisted state so EntriesView opens with the correct date + attorney.
   */
  const navigateToEntriesWithFilter = useCallback((date: Date, profId: string) => {
    // Format date as YYYY-MM-DD for the EntriesView persisted state
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    // Update persisted state that EntriesView reads from
    localStorage.setItem("admin:entries:date", JSON.stringify(dateStr));
    localStorage.setItem("admin:entries:professional", JSON.stringify(profId));

    // Switch to entries view
    setCurrentView("entries");
  }, []);

  const renderView = () => {
    switch (currentView) {
      case "dashboard":
        return <AdminDashboardView />;
      case "billing":
        return <BillingView />;
      case "entries":
        return <EntriesView />;
      case "calendar":
        return <AdminCalendarView onNavigateToEntries={navigateToEntriesWithFilter} />;
      case "support":
        return <AdminSupportView />;
      case "faq":
        return <FAQView />;
      default:
        return <AdminDashboardView />;
    }
  };

  return (
    <div className={`${styles.reportWrapper} ${darkMode ? styles.darkMode : ""}`}>
      <AdminSidebar
        darkMode={darkMode}
        sidebarOpen={sidebarOpen}
        currentView={currentView}
        onChangeView={setCurrentView}
        permissions={canView}
        onSwitchToAttorney={onSwitchToAttorney}
      />
      <div className={`${styles.reportWholeWrapper} ${adminStyles.mainWrapper}`}>
        <div className={`${styles.reportContentWrapper} ${adminStyles.contentWrapper}`}>
          {renderView()}
        </div>
      </div>
    </div>
  );
}
